import { Box, useTheme } from '@mui/material'
import Cookies from 'js-cookie'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

import Navbar from '@/components/navbar'
import useAppInit from '@/hooks/appInit'
import { RootState } from '@/store'
import { useCreateCaseMutation } from '@/store/apiService'
import { setIsLoggingOut } from '@/store/appSlice'
import {
  RecipeGenerationStatus,
  setCurrentCaseId,
  setRecipeGenerationStatus,
} from '@/store/sessionSlice'
import { getCaseId } from '@/utils'
import { getCaseDetailsApi, getMetadataApi } from '@/utils/appApi'
import { NAVBAR_HEIGHT, RECIPE_TITLE } from '@/utils/constants'
import { setCaseIdToDefaultHeaders } from '@/utils/httpService'

interface CreateCaseResponse {
  id: string
  name: string
}

const AppLayout = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const { isAuthenticated } = useAppInit()

  const currentCaseId = useSelector(
    (state: RootState) => state.session.currentCaseId,
  )

  const [createNewCase] = useCreateCaseMutation()

  useEffect(() => {
    const fetchProfileAndOthers = async () => {
      try {
        if (currentCaseId) {
          await getCaseDetailsApi(currentCaseId)
        }
        await getMetadataApi()
      } catch (err: any) {
        if (err?.response?.data?.code === 1001) {
          navigate('/get-started/profile')
        }
      }
    }

    fetchProfileAndOthers()
  }, [isAuthenticated, currentCaseId])

  useEffect(() => {
    const initialCaseId = getCaseId()
    dispatch(setIsLoggingOut(false))
    if (initialCaseId) {
      dispatch(setCurrentCaseId(initialCaseId))
    }
  }, [dispatch])

  const extractProjectId = (pathname: string): string | null => {
    const match = pathname.match(/^\/projects\/([a-f0-9-]{36})$/)
    return match ? match[1] : null
  }

  useEffect(() => {
    const pathname = location.pathname
    const updatedCaseId = extractProjectId(pathname)

    if (updatedCaseId) {
      Cookies.set('case_id', updatedCaseId)
      dispatch(setCurrentCaseId(updatedCaseId))
    }
  }, [location.pathname, dispatch])

  const isValidCaseId = !!currentCaseId

  useEffect(() => {
    const initialCaseId = getCaseId()
    if (!currentCaseId && initialCaseId) {
      return
    }
    const pathname = location.pathname
    const recipePathToCheck = Object.keys(RECIPE_TITLE).find((key) =>
      pathname.includes(key),
    )

    const shouldCreateCase = !!recipePathToCheck && !isValidCaseId

    const initializeCase = async () => {
      if (shouldCreateCase) {
        const newCaseData = {
          id: uuidv4(),
          name: '--',
          case_type: 'auto',
        }

        try {
          const response = (await createNewCase(
            newCaseData,
          ).unwrap()) as CreateCaseResponse
          const newCaseId = response?.id || ''
          Cookies.set('case_id', newCaseId)
          dispatch(setCurrentCaseId(newCaseId))
          setCaseIdToDefaultHeaders(newCaseId)
        } catch (error) {
          console.error('Failed to create new case:', error)
        }
      } else if (isValidCaseId) {
        setCaseIdToDefaultHeaders(currentCaseId!)
      }
    }

    initializeCase()
  }, [location.pathname, isValidCaseId, isAuthenticated])

  // Reset recipe generation status when route changes to avoid it from being stuck in GENERATING state Also Handle cursor on canvas
  useEffect(() => {
    dispatch(setRecipeGenerationStatus(RecipeGenerationStatus.DEFAULT))
  }, [dispatch, location])

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      bgcolor={theme.palette.background.default}
      color={theme.palette.text.primary}
    >
      <Navbar />
      <Box
        display="flex"
        flexGrow={1}
        height={`calc(100vh - ${NAVBAR_HEIGHT}px)`}
      >
        <Outlet />
      </Box>
    </Box>
  )
}

export default AppLayout
